import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Modal,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  TextField,
  Grid,
  Divider,
} from "@mui/material";
import { X } from "react-feather";
import { useIssqn } from "../../../Storage/issqn";
import { useUsuario } from "../../../Hooks/UsuarioHook";
import { useEntidade } from "../../../Hooks/EntidadeHook";
import { useUsuarioPrefeitura } from "../../../Hooks/UsuarioPrefeituraHook";
import { useUsuarioNaoEmiteNota } from "../../../Hooks/UsuarioNaoEmiteNotaHook";
import { geraCodigo } from "../Utils/operacoes";
import { Loader } from "../../Loader";
import { API } from "../../../Services/Api";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 600,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

interface Props {
  open: boolean;
  onClose: () => void;
  periodo: string;
}

export const GerarForm = ({ open, onClose, periodo }: Props) => {
  const { usuario } = useUsuario();
  const { usuarioPrefeitura } = useUsuarioPrefeitura();
  const { codigoFebraban } = useEntidade();
  const { usuarioNaoEmiteNota } = useUsuarioNaoEmiteNota();
  const {
    issqnInformado,
    corrigeCodigoBarras,
    verificaPorPeriodoEFornecedor,
    empresaFocus,
    postIssqnInformado,
  } = useIssqn();

  const [ano, setAno] = useState(new Date().getFullYear().toString());
  const mes = periodo.split("/")[0].padStart(2, "0");
  const [loading, setLoading] = useState(false);
  const [aceitarValor, setAceitarValor] = useState(true);
  const [base, setBase] = useState(0);
  const [valor, setValor] = useState(0);
  const [motivo, setMotivo] = useState("");
  const [padrao, setPadrao] = useState({ base: 0, valor: 0 });

  useEffect(() => {
    if (mes == "12") {
      setAno((new Date().getFullYear() - 1).toString());
    }
  }, [mes]);

  useEffect(() => {
    if (aceitarValor) {
      setBase(padrao.base);
      setValor(padrao.valor);
    }
  }, [aceitarValor, padrao]);

  useEffect(() => {
    if (mes && periodo) {
      setAno(mes === "12" ? `${Number(periodo.split("/")[1]) + 1}` : periodo.split("/")[1]);
    }
  }, [periodo, mes]);

  useEffect(() => {
    if (open) {
      checaPossibilidadeInicial();
      loadData();
    }
  }, [mes, ano, open]);

  async function loadData() {
    try {
      setLoading(true);
      const { data } = await API.post(
        `/calcula-issqn/${usuarioPrefeitura.usuario ? "prefeitura/" : ""}padrao`,
        {
          ano: periodo.split("/")[1].padStart(2, "0"),
          mes: periodo.split("/")[0].padStart(2, "0"),
          fornecedor: empresaFocus.CODIGO_FIRMA || usuarioNaoEmiteNota.CODIGO,
        }
      );
      if (data.length > 0) setPadrao(data[0]);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  }

  async function checaPossibilidadeInicial() {
    const existe = await verificacaoInical();
    if (!existe) onClose();
  }

  async function verificacaoInical() {
    const jaInformado = issqnInformado.find((item) => item.PERIODO.trim() === periodo.trim());
    if (jaInformado) {
      window.alert(`Uma guia do período ${periodo} já foi emitida.`);
      return false;
    }

    const ja_existe_um_carne = await verificaPorPeriodoEFornecedor(
      periodo.padStart(7, "0"),
      empresaFocus.CODIGO_FIRMA || usuarioNaoEmiteNota.CODIGO,
      !!usuarioPrefeitura.usuario
    );

    if (ja_existe_um_carne.length > 0) {
      window.alert(`Já existe guia emitida para o período ${periodo}.`);
      return false;
    }

    if (new Date().getDate() > new Date(defineVencimento()).getDate()) {
      window.alert(`O prazo de pagamento do ISS referente ao período ${periodo} expirou. ${defineVencimento()}`);
      return false;
    }

    return true;
  }

  const mes_definido = () => (mes === "12" ? "1" : String(Number(mes) + 1));

const defineVencimento = () => {
  const hoje = new Date();

  const anoAtual = hoje.getFullYear();
  const mesAtual = hoje.getMonth() + 1; // mês atual: 1 a 12
  const diaHoje = hoje.getDate();

  const diaVencimento = Number(empresaFocus.DIA_VENC || 1);

  let anoVencimento = anoAtual;
  let mesVencimento = mesAtual;

  // Se o dia de vencimento já passou no mês atual,
  // aí sim joga para o próximo mês
  if (diaHoje > diaVencimento) {
    mesVencimento += 1;

    if (mesVencimento > 12) {
      mesVencimento = 1;
      anoVencimento += 1;
    }
  }

  // Último dia do mês definido
  const ultimoDia = new Date(anoVencimento, mesVencimento, 0).getDate();

  // Se DIA_VENC for maior que o último dia do mês, usa o último dia
  const diaFinal = Math.min(diaVencimento, ultimoDia);

  return `${anoVencimento}-${String(mesVencimento).padStart(2, "0")}-${String(diaFinal).padStart(2, "0")}`;
};

  async function verifica() {
    if (!aceitarValor && motivo.trim().length === 0) {
      window.alert("Informe o motivo da alteração dos valores.");
      return false;
    }
    if (valor <= 0) {
      window.alert("Valor inválido para gerar boleto.");
      return false;
    }
    return true;
  }

  async function confirmar() {
    if (!(await verifica())) return;

    const help: ISSQN = {
      BASE: base,
      PERIODO: periodo,
      ISSQN: valor,
      CPFINFORMANTE: usuarioNaoEmiteNota.CODIGO ? "" : (usuario.CPF || usuarioPrefeitura.usuario.cpf),
      DATA_INFORMACAO: '2026-04-06',
      FORNECEDOR: empresaFocus.CODIGO_FIRMA || usuarioNaoEmiteNota.CODIGO,
      COMPUTADA: "N",
      CANCELADA: "N",
      MOTIVO: motivo,
      VENCIMENTO: defineVencimento(),
      CODBARRA: "", // setado depois
      CHAVE: "",
    };

    const vencimento = new Date(defineVencimento());
    const codBarra = geraCodigo(
      help.FORNECEDOR,
      vencimento,
      valor,
      codigoFebraban.toString()
    );
    help.CODBARRA = codBarra;

    const id = await postIssqnInformado(
      help,
      usuarioNaoEmiteNota.CODIGO ? false : !usuario.CPF
    );

    corrigeCodigoBarras(
      Number(id),
      geraCodigo(Number(id), vencimento, valor, codigoFebraban.toString())
    );

    onClose();
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Gerar Guia ISSQN – {periodo}</Typography>
          <X style={{ cursor: "pointer" }} onClick={onClose} />
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={aceitarValor} onChange={() => setAceitarValor(true)} />}
              label="Aceitar valor calculado pelo sistema"
            />
            <FormControlLabel
              control={<Checkbox checked={!aceitarValor} onChange={() => setAceitarValor(false)} />}
              label="Informar valores manualmente"
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Base de Cálculo"
              value={base}
              type="number"
              disabled={aceitarValor}
              onChange={(e) => setBase(Number(e.target.value))}
              InputProps={{
                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
              }}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              label="ISS à Recolher"
              value={valor}
              type="number"
              disabled={aceitarValor}
              onChange={(e) => setValor(Number(e.target.value))}
              InputProps={{
                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
              }}
            />
          </Grid>

          {!aceitarValor && (
            <Grid item xs={12}>
             <TextField
  fullWidth
  label="Motivo"
  value={motivo}
  multiline
  rows={4}
  inputProps={{ maxLength: 500 }} // Define o limite máximo
  onChange={(e) => {
    if (e.target.value.length <= 500) {
      setMotivo(e.target.value);
    }
  }}
/>
            </Grid>
          )}

          <Grid item xs={12} textAlign="center" mt={2}>
            <Button variant="contained" onClick={confirmar}>
              Confirmar
            </Button>
          </Grid>
        </Grid>

        <Loader loading={loading} />
      </Box>
    </Modal>
  );
};

export interface ISSQN {
  PERIODO: string;
  BASE: number;
  CPFINFORMANTE: string;
  DATA_INFORMACAO: string|Date;
  CHAVE: string;
  COMPUTADA: "N" | "C" | "P";
  CANCELADA: "N" | "C";
  MOTIVO: string;
  VENCIMENTO: string;
  CODBARRA: string;
  FORNECEDOR: number;
  ISSQN: number;
}
